import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Login, Register, Forgot, Reset } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import { Team, MemberProfile } from "@/pages/Team";
import { Services, ServicePlanner } from "@/pages/Services";
import { Songs, SongDetail } from "@/pages/Songs";
import Studio from "@/pages/Studio";
import Tools from "@/pages/Tools";
import Practice from "@/pages/Practice";
import Rehearsal from "@/pages/Rehearsal";
import Admin from "@/pages/Admin";
import StagePage from "@/pages/Stage";
import Availability from "@/pages/Availability";
import Tracks from "@/pages/Tracks";
import RunSheet from "@/pages/RunSheet";
import PublicShare from "@/pages/PublicShare";
import PublicRoster from "@/pages/PublicRoster";
import { LogoMark } from "@/components/Logo";

function Splash() {
  return (
    <div className="wf-app-bg min-h-screen flex items-center justify-center">
      <div className="animate-pulse"><LogoMark size={56} /></div>
    </div>
  );
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready || user === null) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password" element={<Reset />} />
      <Route path="/share/:shareId" element={<PublicShare />} />
      <Route path="/roster/:shareId" element={<PublicRoster />} />

      <Route path="/stage" element={<Protected><StagePage /></Protected>} />
      <Route path="/stage/:songId" element={<Protected><StagePage /></Protected>} />
      <Route path="/services/:id/runsheet" element={<Protected><RunSheet /></Protected>} />

      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:id" element={<ServicePlanner />} />
        <Route path="/songs" element={<Songs />} />
        <Route path="/songs/:id" element={<SongDetail />} />
        <Route path="/team" element={<Team />} />
        <Route path="/team/:id" element={<MemberProfile />} />
        <Route path="/studio" element={<Studio />} />
        <Route path="/tools" element={<Tools />} />
        <Route path="/practice" element={<Practice />} />
        <Route path="/rehearsal" element={<Rehearsal />} />
        <Route path="/availability" element={<Availability />} />
        <Route path="/tracks" element={<Tracks />} />
        <Route path="/admin" element={<Admin />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" theme="dark" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
